"use client";

import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";

const NAVBAR_LINE_COLOR = "#2e1065";
const CAROUSEL_INTERVAL_MS = 5000;
const SLIDE_DURATION_MS = 400;

export interface WVideoStripeItem {
  youtubeUrl?: string;
  title?: string;
}

export interface WVideoStripeSectionProps {
  heading?: string;
  showTitle?: boolean;
  videos?: WVideoStripeItem[];
}

function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") {
      return u.pathname.slice(1) || null;
    }
    if (
      u.hostname === "www.youtube.com" ||
      u.hostname === "youtube.com" ||
      u.hostname === "m.youtube.com"
    ) {
      const v = u.searchParams.get("v");
      if (v) return v;
      const parts = u.pathname.split("/");
      const idx = parts.indexOf("embed");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
  } catch {
    return null;
  }
  return null;
}

export default function WVideoStripeSection({
  heading,
  showTitle = true,
  videos = [],
}: WVideoStripeSectionProps) {
  const displayTitle =
    heading != null && String(heading).trim() !== ""
      ? String(heading).trim()
      : "";
  const showTitleBlock = showTitle !== false && displayTitle !== "";

  const items = (videos || [])
    .map((v) => {
      const raw = v?.youtubeUrl ? String(v.youtubeUrl).trim() : "";
      const id = raw ? getYouTubeId(raw) : null;
      if (!raw || !id) return null;
      return {
        youtubeUrl: raw,
        id,
        title: v?.title || "",
      };
    })
    .filter(Boolean) as Array<{ youtubeUrl: string; id: string; title: string }>;

  const length = items.length;
  const trackItems = length > 0 ? [...items, ...items] : [];
  const trackLength = trackItems.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [suppressTransition, setSuppressTransition] = useState(false);

  const goNext = useCallback(() => {
    if (length <= 1) return;
    if (currentIndex < trackLength - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setSuppressTransition(true);
      setCurrentIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSuppressTransition(false);
        });
      });
    }
  }, [length, trackLength, currentIndex]);

  const goPrev = useCallback(() => {
    if (length <= 1) return;
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else {
      setSuppressTransition(true);
      setCurrentIndex(length);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSuppressTransition(false);
          setCurrentIndex(length - 1);
        });
      });
    }
  }, [length, currentIndex]);

  useEffect(() => {
    if (length <= 1) return;
    const id = setInterval(goNext, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [length, goNext]);

  const hasVideos = length > 0;
  const displayIndex = trackLength > 0 ? (currentIndex % length) + 1 : 0;

  return (
    <section className="w-full py-6 md:py-8">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        {showTitleBlock && (
          <div className="mb-6 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-end"
              style={{
                background: `linear-gradient(to right, transparent, ${NAVBAR_LINE_COLOR})`,
              }}
              aria-hidden
            />
            <h2 className="text-center font-extrabold uppercase tracking-wide text-violet-950 md:text-xl lg:text-2xl">
              {displayTitle}
            </h2>
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-start"
              style={{
                background: `linear-gradient(to right, ${NAVBAR_LINE_COLOR}, transparent)`,
              }}
              aria-hidden
            />
          </div>
        )}

        {/* Viewport: 2 videos on mobile, 3 on desktop */}
        <div className="relative h-[180px] w-full overflow-hidden rounded-xl md:h-[215px]">
          <div
            className={`w-photo-stripe-track flex h-full flex-nowrap gap-6 ${
              suppressTransition ? "no-transition" : ""
            }`}
            style={
              { "--photo-index": hasVideos ? currentIndex : 0 } as React.CSSProperties
            }
          >
            {hasVideos ? (
              trackItems.map((item, index) => {
                const thumb = `https://img.youtube.com/vi/${item.id}/hqdefault.jpg`;
                const label =
                  item.title && item.title.trim() !== ""
                    ? item.title.trim()
                    : `Video ${(index % length) + 1}`;

                return (
                  <div
                    key={`${index}-${item.youtubeUrl}`}
                    className="w-photo-stripe-slot relative flex h-[180px] flex-shrink-0 md:h-[215px]"
                  >
                    <a
                      href={item.youtubeUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="relative block h-full w-full overflow-hidden rounded-xl bg-black"
                      aria-label={label}
                    >
                      <Image
                        src={thumb}
                        alt={label}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 50vw, 33vw"
                        unoptimized
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 shadow-lg">
                          <div className="ml-1 border-y-[9px] border-l-[14px] border-y-transparent border-l-white" />
                        </div>
                      </div>
                    </a>
                  </div>
                );
              })
            ) : (
              <div className="flex w-full items-center justify-center rounded-xl bg-gray-100 py-16 text-center text-gray-500">
                <p>No videos yet. Add YouTube links in the W-Video Stripe section in Sanity.</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom: left arrow, 1/10, right arrow */}
        {hasVideos && (
          <div className="mt-4 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={goPrev}
              className="flex shrink-0 items-center justify-center p-1 text-gray-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-40"
              aria-label="Previous video"
            >
              <span aria-hidden>←</span>
            </button>
            <div className="text-sm text-gray-500">
              {displayIndex} / {length}
            </div>
            <button
              type="button"
              onClick={goNext}
              className="flex shrink-0 items-center justify-center p-1 text-gray-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-40"
              aria-label="Next video"
            >
              <span aria-hidden>→</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

